import { useEffect, useState } from "react";
import { CheckCircle2, Scale } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  fmtMoney2,
  monthLabel,
  previousMonth,
  recentMonths,
  usePaidAmounts,
  useRecordPaidAmount,
  type PaidAmountRow,
} from "../api";

/**
 * The financials-reconciliation view: for each payee, what the system computed
 * as payable vs what was ACTUALLY paid per the books — entered by hand
 * (admin or the narrow commissions.write grant) with a note for any variance.
 */
export function PaidView() {
  const [period, setPeriod] = useState(previousMonth());
  const { data, error, isLoading } = usePaidAmounts(period);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Month
          </span>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue>{monthLabel(period)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {recentMonths(24).map((m) => (
                <SelectItem key={m} value={m}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {data && (
          <div className="flex items-center gap-4 text-sm tabular-nums">
            <span>
              System <span className="font-semibold">{fmtMoney2(data.total_system)}</span>
            </span>
            <span>
              Paid <span className="font-semibold">{fmtMoney2(data.total_paid)}</span>
            </span>
            <VarianceBadge value={round2(data.total_paid - data.total_system)} />
          </div>
        )}
      </div>

      <p className="max-w-2xl text-sm text-muted-foreground">
        Record the total commission actually paid to each person for the month —
        straight from the books — so the system's computed payouts reconcile with
        the financials. Add a note whenever the two differ.
      </p>

      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load paid amounts</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {isLoading && !data && <Card className="h-40 animate-pulse" />}

      {data && data.rows.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <Scale className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">Nothing to reconcile for {monthLabel(period)}</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Run the month on the Monthly Report tab first — its payees appear
            here with the computed amounts to reconcile against.
          </p>
        </Card>
      )}

      {data && data.rows.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payee</TableHead>
                  <TableHead align="right">System payable</TableHead>
                  <TableHead align="right">Amount paid</TableHead>
                  <TableHead align="right">Variance</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Recorded by</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => (
                  <PaidRow key={row.payee_id} row={row} period={period} />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow className="font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell numeric>{fmtMoney2(data.total_system)}</TableCell>
                  <TableCell numeric>{fmtMoney2(data.total_paid)}</TableCell>
                  <TableCell numeric>
                    <VarianceBadge value={round2(data.total_paid - data.total_system)} />
                  </TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function VarianceBadge({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  if (value === 0) {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        {fmtMoney2(0)}
      </Badge>
    );
  }
  return <Badge variant="warning">{fmtMoney2(value)}</Badge>;
}

function PaidRow({ row, period }: { row: PaidAmountRow; period: string }) {
  const record = useRecordPaidAmount(period);
  const [amount, setAmount] = useState(row.amount_paid != null ? String(row.amount_paid) : "");
  const [note, setNote] = useState(row.note ?? "");
  // Server value changed (e.g. after save or month switch) → resync inputs.
  useEffect(() => {
    setAmount(row.amount_paid != null ? String(row.amount_paid) : "");
    setNote(row.note ?? "");
  }, [row.amount_paid, row.note]);

  const parsed = amount.trim() === "" ? null : Number(amount);
  const invalid = parsed != null && (Number.isNaN(parsed) || parsed < 0);
  const dirty =
    (parsed ?? null) !== (row.amount_paid ?? null) || note.trim() !== (row.note ?? "");

  const save = () => {
    if (parsed == null || invalid) return;
    record.mutate(
      { payee_id: row.payee_id, amount_paid: parsed, note: note.trim() || null },
      {
        onSuccess: () => toast.success(`${row.payee_name} recorded`),
        onError: (e) => toast.error("Couldn't save", { description: (e as Error).message }),
      },
    );
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{row.payee_name}</TableCell>
      <TableCell numeric className="tabular-nums">
        {fmtMoney2(row.system_payable)}
      </TableCell>
      <TableCell numeric>
        <Input
          className={cn("ml-auto h-8 w-28 text-right tabular-nums", invalid && "border-danger")}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          aria-label={`Amount paid to ${row.payee_name}`}
        />
      </TableCell>
      <TableCell numeric>
        <VarianceBadge
          value={parsed != null && !invalid ? round2(parsed - row.system_payable) : null}
        />
      </TableCell>
      <TableCell>
        <Input
          className="h-8 min-w-[220px]"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why it differs (if it does)"
          aria-label={`Note for ${row.payee_name}`}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
        {row.recorded_by_name ?? "—"}
      </TableCell>
      <TableCell className="text-right">
        <Button
          size="sm"
          variant={dirty ? "default" : "outline"}
          disabled={parsed == null || invalid || !dirty || record.isPending}
          onClick={save}
        >
          {record.isPending ? "Saving…" : "Save"}
        </Button>
      </TableCell>
    </TableRow>
  );
}
